import {
  Box,
  Button,
  CssBaseline,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { TranslateText } from '../../../../../application/language/TranslateText';
import { useI18n } from '../../../../../application/language/useI18n';
import { observer } from 'mobx-react';
import React, { useCallback } from 'react';
import { useParams } from 'react-router';
import { PageLoader } from '../../../../design/page/PageLoader';

import { IMainLayoutDomainStore } from '../../../../layout/main/store/domain/IMainLayoutDomainStore';
import { IProjectInvitation } from './store/IProjectInvatation';
import { ProjectInvitationJoinDomain } from './store/ProjectInvitationJoinDomain';
import { ProjectInvitationJoinUI } from './store/ProjectInvitationJoinUI';
import { Colors } from '../../../../design/color/Colors';

interface RoleSelectionProps {
  roles: IProjectInvitation['abledRoles'];
  value: string | null;
  onChange: (event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string | null>) => void;
}

const RoleSelection: React.FC<RoleSelectionProps> = ({ roles, onChange, value }) => {
  if (roles.length === 1) return null;

  if (roles.length > 5) {
    return (
      <FormControl variant="standard">
        <InputLabel variant="standard">
          <TranslateText i18nKey="invitation.join.youAreInProject" />
        </InputLabel>
        <Select
          value={value}
          label={<TranslateText i18nKey="invitation.join.youAreInProject" />}
          onChange={onChange}
          variant="standard"
        >
          {roles.map(({ id, name }) => (
            <MenuItem value={id} key={name + id}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <RadioGroup value={value} onChange={onChange}>
      {roles.map(({ id, name }) => (
        <FormControlLabel
          value={id}
          key={name + id}
          control={
            <Radio
              size="small"
              sx={{
                '&.Mui-checked': { color: Colors.primary },
                '& .MuiSvgIcon-root': { height: 22, width: 22 },
                '&:hover': { backgroundColor: 'transparent' },
                '& .MuiTouchRipple-root': { display: 'none' },
              }}
            />
          }
          label={name}
        />
      ))}
    </RadioGroup>
  );
};

const TOP_PADDING = '70px';

interface Props {
  layoutDomain: IMainLayoutDomainStore;
}

export const ProjectInvitationJoinPage = observer(({ layoutDomain }: Props) => {
  const [{ isBooting, projectInvatation, boot, notFoundInvatation, chosenRole, join, isJoining }] = React.useState(
    () => new ProjectInvitationJoinUI(layoutDomain, new ProjectInvitationJoinDomain(layoutDomain)),
  );
  const { address } = useParams<{ address: string }>();
  const { translate } = useI18n();
  const labelInvatation = React.useMemo(() => {
    const extensionLabel = translate('invitation.join.eduDescription');

    const roleLabel =
      projectInvatation.value?.abledRoles.length === 1
        ? `${translate('invitation.join.toRole', { role: projectInvatation.value?.abledRoles[0].name })} `
        : '';
    const label = `${extensionLabel} ${roleLabel}`;

    return label;
  }, [projectInvatation.value?.abledRoles, translate]);

  const onChangeInputRole = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string | null>) => {
      chosenRole.setValue(event.target.value);
    },
    [chosenRole],
  );

  React.useEffect(() => {
    if (address) {
      boot(address);
    } else {
      notFoundInvatation();
    }
  }, [boot, address, notFoundInvatation]);

  if (isBooting.value) {
    return (
      <Box display="flex" justifyContent="center" paddingTop={TOP_PADDING}>
        <CssBaseline />
        <PageLoader minHeight="calc(100vh - 160px)" />
      </Box>
    );
  }

  if (!projectInvatation.value) {
    return null;
  }

  const projectName = projectInvatation.value?.project.name || '';
  const roles = projectInvatation.value?.abledRoles || [];

  return (
    <Box paddingTop={TOP_PADDING} display="flex" justifyContent="center">
      <CssBaseline />
      <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" gap="32px">
        <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center">
          <Typography component="h1" variant="h5" textAlign="center" maxWidth="480px">
            <TranslateText i18nKey="invitation.join.welcome" />
          </Typography>
          <Typography
            component="h1"
            variant={projectName.length > 90 ? 'h2' : 'h1'}
            textAlign="center"
            maxWidth="1500px"
          >
            {projectName}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="center" flexDirection="column" gap="64px" maxWidth="480px">
          <Box display="flex" justifyContent="center" flexDirection="column" gap="24px">
            <Typography component="h3">{labelInvatation}</Typography>

            <RoleSelection roles={roles} onChange={onChangeInputRole} value={chosenRole.value} />
          </Box>

          <Button disabled={isJoining.value} onClick={join} variant="contained">
            <TranslateText i18nKey="invitation.join.joinBtn"></TranslateText>
          </Button>
        </Box>
      </Box>
    </Box>
  );
});
